import { useBounds } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import { useCallback, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { Vector3 } from 'three'

import { isTestMode } from '@utils/env.ts'

import type { TransformedShelf } from '@state/shelf/types'

export function getShelfQuaternion(quaternion: TransformedShelf['rotation']) {
  return new THREE.Quaternion(
    quaternion.x,
    -quaternion.z,
    quaternion.y,
    quaternion.w
  )
}

interface loadTextureOptions {
  imageUrl: string
  size: number
  canvasMutationCallback?: (img: HTMLImageElement, ctx: CanvasRenderingContext2D) => void
}

export function loadTexture({ imageUrl, size, canvasMutationCallback }: loadTextureOptions): Promise<THREE.CanvasTexture | undefined> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!

    const img = new Image()

    // yay, CORS!
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      canvas.width = size
      canvas.height = size

      if (canvasMutationCallback) {
        // via this callback you can influence how the image is pasted onto the texture and apply e.g. padding, background color, etc.
        try {
          canvasMutationCallback(img, ctx)
        } catch (e) {
          return reject(e instanceof Error ? e : new Error(String(e)))
        }
      } else {
        // just draw the image onto the canvas
        ctx.drawImage(img, 0, 0, size, size)
      }

      resolve(canvasToTexture(canvas))
    }

    img.onerror = () => {
      reject(new Error(`Failed to load image: ${imageUrl}`))
    }

    img.src = imageUrl
  })
}

function canvasToTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.generateMipmaps = false
  texture.needsUpdate = true

  return texture
}

export function useBobbing() {
  const posRef = useRef<Vector3 | null>(null)
  const groupRef = useRef<THREE.Group | null>(null)

  const myRef = useCallback((groupInstance: THREE.Group | null) => {
    if (!groupInstance) return

    groupRef.current = groupInstance
    posRef.current = groupInstance.position.clone()
  }, [])

  const phaseOffset = useMemo(() => {
    // we need to make the offset deterministic to not throw off screenshot tests
    if (isTestMode) return 1

    // eslint-disable-next-line react-hooks/purity
    return Math.random() * Math.PI
  }, [])

  useFrame((state) => {
    if (!groupRef.current || !posRef.current) return

    const bobOffset = Math.sin(state.clock.elapsedTime + phaseOffset) * 0.1
    groupRef.current.position.y = posRef.current.y + bobOffset
  })

  return myRef
}

function isObjectVisible(object: THREE.Object3D, camera: THREE.Camera) {
  const box = new THREE.Box3().setFromObject(object)

  const frustum = new THREE.Frustum()
  const matrix = new THREE.Matrix4().multiplyMatrices(
    camera.projectionMatrix,
    camera.matrixWorldInverse
  )
  frustum.setFromProjectionMatrix(matrix)

  return frustum.intersectsBox(box)
}

export function useZoomToObject() {
  const { camera } = useThree()
  const boundsApi = useBounds()

  return useCallback((object: THREE.Object3D) => {
    if (isObjectVisible(object, camera)) return

    boundsApi.refresh(object).fit().clip()
  }, [camera, boundsApi])
}